/**
 * Call preferences remembered per browser (TRUS-668 v3 §1 + §2): the chosen
 * camera / microphone / speaker, mirror + hide self view, the layout and the
 * self-view corner; sound cues and desktop notifications for calls. localStorage is best effort: a private window or blocked
 * site data throws, and the call then simply uses the defaults.
 */

import { useSyncExternalStore } from 'react';

export type CallLayout = 'grid' | 'speaker' | 'sidebar';
export type SelfViewCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface CallPrefs {
  cameraId: string | null;
  micId: string | null;
  speakerId: string | null;
  /** Show my own video mirrored (like a mirror). On by default. */
  mirrorSelf: boolean;
  hideSelf: boolean;
  layout: CallLayout;
  selfCorner: SelfViewCorner;
  /** Short tones for mute / camera / someone joined or left. On by default. */
  soundCues: boolean;
  /** Desktop notifications for calls (ringing, joined, declined, no answer). On by default. */
  desktopNotify: boolean;
}

export const CALL_PREFS_KEY = 'weavein.calls.prefs.v1';

export const DEFAULT_CALL_PREFS: CallPrefs = {
  cameraId: null,
  micId: null,
  speakerId: null,
  mirrorSelf: true,
  hideSelf: false,
  layout: 'grid',
  selfCorner: 'bottom-right',
  soundCues: true,
  desktopNotify: true,
};

const LAYOUTS: CallLayout[] = ['grid', 'speaker', 'sidebar'];
const CORNERS: SelfViewCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

export function sanitizeCallPrefs(raw: unknown): CallPrefs {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    cameraId: str(r.cameraId),
    micId: str(r.micId),
    speakerId: str(r.speakerId),
    mirrorSelf: typeof r.mirrorSelf === 'boolean' ? r.mirrorSelf : DEFAULT_CALL_PREFS.mirrorSelf,
    hideSelf: typeof r.hideSelf === 'boolean' ? r.hideSelf : DEFAULT_CALL_PREFS.hideSelf,
    layout: LAYOUTS.includes(r.layout as CallLayout) ? (r.layout as CallLayout) : DEFAULT_CALL_PREFS.layout,
    selfCorner: CORNERS.includes(r.selfCorner as SelfViewCorner)
      ? (r.selfCorner as SelfViewCorner)
      : DEFAULT_CALL_PREFS.selfCorner,
    soundCues: typeof r.soundCues === 'boolean' ? r.soundCues : DEFAULT_CALL_PREFS.soundCues,
    desktopNotify: typeof r.desktopNotify === 'boolean' ? r.desktopNotify : DEFAULT_CALL_PREFS.desktopNotify,
  };
}

function load(): CallPrefs {
  try {
    const raw = window.localStorage.getItem(CALL_PREFS_KEY);
    return raw ? sanitizeCallPrefs(JSON.parse(raw)) : DEFAULT_CALL_PREFS;
  } catch {
    return DEFAULT_CALL_PREFS;
  }
}

let prefs: CallPrefs | null = null;
const listeners = new Set<() => void>();

export const callPrefsStore = {
  get(): CallPrefs {
    if (!prefs) prefs = load();
    return prefs;
  },
  set(patch: Partial<CallPrefs>) {
    prefs = sanitizeCallPrefs({ ...callPrefsStore.get(), ...patch });
    try {
      window.localStorage.setItem(CALL_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      /* not persisted — still applies to this call */
    }
    listeners.forEach((l) => l());
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Tests only: forget the in-memory copy (re-read storage next time). */
  reset() {
    prefs = null;
    listeners.forEach((l) => l());
  },
};

export function useCallPrefs(): [CallPrefs, (patch: Partial<CallPrefs>) => void] {
  const value = useSyncExternalStore(callPrefsStore.subscribe, callPrefsStore.get, callPrefsStore.get);
  return [value, callPrefsStore.set];
}
